/**
 * accessVmBuilder — traduz o `GET /access` (e o estado da tela) no ViewModel do contrato @v3.
 *
 * É uma função PURA de propósito: é a peça que decide o que a tela mostra, e precisa ser testável
 * sem montar React. O container (AccessPage) guarda o estado e chama a API; tudo que é tradução
 * mora aqui.
 *
 * Contrato: `access.vm.js` (@version 3). Toda string que chega na View sai formatada daqui — a
 * View nunca calcula, nunca deriva, nunca formata.
 */
import { formatDateTime } from '../../utils/formatters';
import { getInitials } from '../../utils/professionalNameUtils';

export const ENTIDADES = [
  { key: 'professional', label: 'Profissional', noNone: true },
  { key: 'appointments', label: 'Agendamentos', noNone: false },
  { key: 'schedules', label: 'Agendas', noNone: false },
  { key: 'persons', label: 'Clientes', noNone: false },
];

const COLUMN_LABELS = {
  type: 'Tipo', professional: 'Profissional', sharedWith: 'Compartilhado com',
  permissions: 'Permissões', createdAt: 'Criado em', updatedAt: 'Editado em', actions: 'Ações',
};

export const DICA_REGRAS = 'Profissional precisa de pelo menos Ver. Para Editar agendamentos, Agendas precisa de pelo menos Ver.';
const ROTULO_NIVEL = { 1: 'Ver', 2: 'Editar' };

// Os inteiros vêm do enum AccessEntityType da API (Domain/Enums/AccessEntityType.cs) — não inventar aqui.
const TIPO_DA_ENTIDADE = { professional: 1, appointments: 2, schedules: 3, persons: 4 };
const ENTIDADE_DO_TIPO = { 1: 'professional', 2: 'appointments', 3: 'schedules', 4: 'persons' };

/** Novo compartilhamento começa no mínimo que o servidor aceita: Profissional em Ver, o resto Nenhum. */
export const PERMISSOES_INICIAIS = { professional: 1, appointments: 0, schedules: 0, persons: 0 };

export const FORM_FECHADO = {
  aberto: false, profissionalId: '', email: '', erroEmail: null, buscando: false,
  resultado: null, permissoes: PERMISSOES_INICIAIS, compartilhando: false,
};

// O GET /access serializa enum como TEXTO camelCase (JsonStringEnumConverter(CamelCase) no Program.cs
// da API): entityType "appointments", accessLevel "readWrite". Número e PascalCase também valem — a
// tela antiga só conhecia esses dois e lia "readWrite" como Nenhum.
export function entidadeDe(entityType) {
  if (typeof entityType === 'number') return ENTIDADE_DO_TIPO[entityType] ?? null;
  const texto = String(entityType ?? '');
  const chave = texto.charAt(0).toLowerCase() + texto.slice(1);
  return chave in TIPO_DA_ENTIDADE ? chave : null;
}

export function nivelDe(accessLevel) {
  if (accessLevel === 2 || /^readwrite$/i.test(String(accessLevel))) return 2;
  if (accessLevel === 1 || /^read$/i.test(String(accessLevel))) return 1;
  return 0;
}

/** Lista de permissões da API → nível de cada uma das 4 áreas (área que não veio = 0, não vê). */
export function mapaDePermissoes(permissions) {
  const mapa = { professional: 0, appointments: 0, schedules: 0, persons: 0 };
  for (const p of permissions || []) {
    const chave = entidadeDe(p.entityType);
    if (chave) mapa[chave] = nivelDe(p.accessLevel);
  }
  return mapa;
}

/** Mapa → corpo do POST/PUT: só as áreas com acesso, em inteiros do enum (a API aceita número). */
export function permissoesParaApi(mapa) {
  return ENTIDADES.filter((e) => mapa[e.key] > 0).map((e) => ({ entityType: TIPO_DA_ENTIDADE[e.key], accessLevel: mapa[e.key] }));
}

/**
 * As regras que o servidor valida (ProfessionalAccessService), aplicadas na hora para a pessoa nunca
 * montar um pedido que vai ser recusado: Profissional nunca abaixo de Ver; Agendamentos em Editar
 * puxa Agendas para pelo menos Ver; tirar Agendas derruba Agendamentos de Editar para Ver.
 */
export function aplicarRegras(mapa, chave, nivel) {
  const novo = { ...mapa, [chave]: nivel };
  if (chave === 'professional') novo.professional = Math.max(nivel, 1);
  if (chave === 'appointments' && nivel === 2 && novo.schedules === 0) novo.schedules = 1;
  if (chave === 'schedules' && nivel === 0 && novo.appointments === 2) novo.appointments = 1;
  return novo;
}

/** Identidade da linha: um compartilhamento = um profissional × quem recebeu. */
export const chaveDaLinha = (item) => `${item.professionalId}-${item.grantedToUserId}`;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const validarEmail = (email) => (email && !EMAIL.test(email) ? 'E-mail inválido' : null);

const dataOuTraco = (iso) => (iso && formatDateTime(iso)) || '—';

/**
 * Os textos de remover, do lado de quem está na tela. O DELETE aceita as duas partes
 * (AccessController » RevokeAccessByEitherPartyAsync): o dono remove a pessoa; quem recebeu SAI.
 * Para quem recebeu, "Remover o acesso de João Silva?" seria falso — quem perde o acesso é ele.
 */
export function textosDeRemocao(item) {
  if (item.direction !== 'given') {
    return {
      actionLabel: 'Sair do compartilhamento',
      title: `Sair do compartilhamento de ${item.professionalName}?`,
      body: `Você deixa de ver ${item.professionalName} assim que confirmar. Para voltar, ${item.ownerName} precisa compartilhar de novo.`,
      confirmLabel: 'Sair', cancelLabel: 'Continuar vendo', progressLabel: 'Saindo…',
    };
  }
  return {
    actionLabel: 'Remover acesso',
    title: `Remover o acesso de ${item.grantedToName}?`,
    body: `${item.grantedToName} deixa de ver ${item.professionalName} assim que você confirmar. Se mudar de ideia, é só compartilhar de novo.`,
    confirmLabel: 'Remover acesso', cancelLabel: 'Manter acesso', progressLabel: 'Removendo…',
  };
}

const textoOuNulo = (texto) => (typeof texto === 'string' && texto.trim()) || null;

/**
 * Uma AccessRow a partir de um item do GET /access (AccessOverviewItemDto, camelCase).
 * `fotoDe(donoId, profissionalId)` monta a URL da foto (photoService) — injetada para a função seguir pura.
 */
export function montarLinha(item, { modo = 'idle', editPermissions, salvando = false, removendo = false, fotoDe }) {
  const recebido = item.direction !== 'given';
  const niveis = mapaDePermissoes(item.permissions);
  return {
    key: chaveDaLinha(item),
    direction: recebido ? 'received' : 'given',
    directionLabel: recebido ? 'Recebido' : 'Enviado',
    professionalId: item.professionalId,
    professionalName: item.professionalName,
    professionalSpecialty: textoOuNulo(item.professionalSpecialty),
    // A foto mora no bucket do DONO do profissional (ProfessionalAvatar) — vale para as duas direções.
    professionalPhotoUrl: fotoDe(item.ownerUserId, item.professionalId),
    professionalInitials: getInitials(item.professionalName),
    ownerNote: recebido ? `Dono: ${item.ownerName}` : null,
    sharedWithName: recebido ? item.ownerName : item.grantedToName,
    sharedWithEmail: recebido ? item.ownerEmail : item.grantedToEmail,
    permissions: ENTIDADES.filter((e) => niveis[e.key] > 0).map((e) => ({
      label: `${e.label}: ${ROTULO_NIVEL[niveis[e.key]]}`,
      tone: niveis[e.key] === 2 ? 'success' : 'neutral',
    })),
    permissionLevels: niveis,
    createdAtLabel: dataOuTraco(item.createdAt),
    updatedAtLabel: dataOuTraco(item.updatedAt),
    canEdit: !recebido,
    revoke: textosDeRemocao(item),
    mode: modo,
    ...(modo === 'editing' ? { editPermissions } : {}),
    saving: modo === 'editing' && salvando,
    revoking: modo === 'revoking' && removendo,
  };
}

/**
 * O vm inteiro.
 * @param {object} p
 * @param {object[]} p.itens          itens do GET /access
 * @param {boolean} p.carregando      primeira carga
 * @param {boolean} p.erro            a primeira carga falhou
 * @param {object} p.form             estado do formulário (forma de FORM_FECHADO)
 * @param {object[]} p.profissionais  profissionais do dono (GET /professionals, sem os compartilhados)
 * @param {string} p.usuarioId        quem está logado — dono dos próprios profissionais (bucket da foto)
 * @param {{key:string, permissoes:object, salvando:boolean}|null} p.editando
 * @param {{key:string, emAndamento:boolean}|null} p.removendo
 * @param {(donoId:string, profissionalId:string)=>string} p.fotoDe
 */
export function montarVm({ itens, carregando, erro, form, profissionais, usuarioId, editando, removendo, fotoDe }) {
  const rows = (itens || []).map((item) => {
    const key = chaveDaLinha(item);
    if (editando?.key === key) return montarLinha(item, { modo: 'editing', editPermissions: editando.permissoes, salvando: editando.salvando, fotoDe });
    if (removendo?.key === key) return montarLinha(item, { modo: 'revoking', removendo: removendo.emAndamento, fotoDe });
    return montarLinha(item, { fotoDe });
  });
  const achou = !!form.resultado?.found;
  return {
    viewState: carregando ? 'loading' : erro ? 'error' : rows.length === 0 ? 'empty' : 'ready',
    errorMessage: erro ? 'Erro ao carregar acessos' : null,
    title: 'Gestão de Acessos',
    subtitle: 'Compartilhe profissionais com outras contas e controle o que cada pessoa pode ver ou editar',
    toggleLabel: form.aberto ? 'Cancelar' : 'Compartilhar Acesso',
    entities: ENTIDADES,
    grantForm: {
      open: form.aberto,
      professionals: (profissionais || []).map((p) => ({
        id: p.id, name: p.name, photoUrl: fotoDe(usuarioId, p.id), initials: getInitials(p.name), specialty: textoOuNulo(p.specialty),
      })),
      selectedProfessionalId: form.profissionalId,
      email: form.email,
      emailError: form.erroEmail,
      searching: form.buscando,
      searchResult: form.resultado
        ? { found: achou, userName: achou ? form.resultado.user.name : null, userEmail: achou ? form.resultado.user.email : null }
        : null,
      permissions: form.permissoes,
      granting: form.compartilhando,
      canGrant: achou && !!form.profissionalId,
      rulesHint: DICA_REGRAS,
    },
    rows,
    emptyMessage: 'Nenhum compartilhamento encontrado. Use o botão acima para compartilhar um profissional.',
    columnLabels: COLUMN_LABELS,
  };
}
